// PORTED COPY — A20 W2b. Original: TPRM7 packages/ui/src/reporter/strings.ts
// (@vendorwatch/ui). Byte-identical to the original below this header; the
// licence repo cannot import @vendorwatch/ui, so it carries a copy the way it
// already carries a copy of the DS token CSS. Translate upstream first.
//
// A20 · W3 — the reporter's own string catalogue.
//
// WHY THIS IS NOT AN i18n LIBRARY. There is zero internationalisation anywhere
// in this codebase today: no next-intl / react-i18next / formatjs, no locales
// directory, no Next i18n config, and `lang="en"` is hardcoded in all three root
// layouts. Introducing a library would mean a dependency, a provider, and a
// bundle cost in five apps — four of which would use it for nothing but this
// widget.
//
// So the widget owns its strings. The shape follows the one precedent this repo
// has for externalised copy (apps/admin/src/lib/help-content.ts): a flat typed
// object literal. Adding a locale is a data change with no wiring.
//
// English is the base AND the per-key fallback: a missing key renders the
// English string, never a raw key. Translations may therefore be partial.

export type StringKey =
  | 'button.label'
  | 'dialog.title'
  | 'dialog.subtitle'
  | 'field.title.label'
  | 'field.title.placeholder'
  | 'field.description.label'
  | 'field.description.placeholder'
  | 'field.severity.label'
  | 'severity.blocker'
  | 'severity.major'
  | 'severity.minor'
  | 'severity.question'
  | 'capture.heading'
  | 'capture.on'
  | 'capture.off'
  | 'capture.noreplay'
  | 'capture.masked'
  | 'capture.revealed'
  | 'reveal.label'
  | 'reveal.hint'
  | 'context.heading'
  | 'action.submit'
  | 'action.submitting'
  | 'action.cancel'
  | 'action.close'
  | 'result.ok'
  | 'result.queued'
  | 'result.error'
  | 'error.required'
  | 'privacy.note'
  | 'studio.title'
  | 'studio.subtitle'
  | 'studio.name.label'
  | 'studio.name.placeholder'
  | 'studio.action.start'
  | 'studio.action.save';

type Catalogue = Partial<Record<StringKey, string>>;

const en: Record<StringKey, string> = {
  'button.label': 'Report a problem',
  'dialog.title': 'Report a problem',
  'dialog.subtitle': 'Tell us what went wrong. We attach the last moments of your session so we can reproduce it.',
  'field.title.label': 'Summary',
  'field.title.placeholder': 'What went wrong, in one line',
  'field.description.label': 'What happened',
  'field.description.placeholder': 'What were you doing, what did you expect, and what happened instead?',
  'field.severity.label': 'Impact',
  'severity.blocker': 'Blocks my work',
  'severity.major': 'Major problem',
  'severity.minor': 'Minor issue',
  'severity.question': 'Question',
  'capture.heading': 'Session capture',
  'capture.on': 'The last {seconds}s of this page will be attached.',
  'capture.off': 'Session capture is unavailable. Your report will still be sent.',
  'capture.noreplay':
    'This page does not record the screen. Your report carries the page address plus the recent console and network activity.',
  'capture.masked': 'All text and typed values are hidden.',
  'capture.revealed': 'Visible text is included. Typed values stay hidden.',
  'reveal.label': 'Include visible text',
  'reveal.hint': 'Restarts the capture — please reproduce the problem again before sending.',
  'context.heading': 'Attached automatically',
  'action.submit': 'Send report',
  'action.submitting': 'Sending…',
  'action.cancel': 'Cancel',
  'action.close': 'Close',
  'result.ok': 'Thank you — your report was received.',
  'result.queued': 'Saved. It will be sent the next time this system connects.',
  'result.error': 'We could not send the report: {error}',
  'error.required': 'Please describe what happened.',
  'privacy.note': 'Passwords and other typed values are never captured.',
  'studio.title': 'Record a walkthrough',
  'studio.subtitle': 'Capture a narrated walkthrough to publish as help content.',
  'studio.name.label': 'Walkthrough name',
  'studio.name.placeholder': 'e.g. Publishing a Trust Center profile',
  'studio.action.start': 'Start recording',
  'studio.action.save': 'Save walkthrough',
};

const es: Catalogue = {
  'button.label': 'Informar de un problema',
  'dialog.title': 'Informar de un problema',
  'dialog.subtitle': 'Cuéntanos qué ha fallado. Adjuntamos los últimos momentos de tu sesión para poder reproducirlo.',
  'field.title.label': 'Resumen',
  'field.title.placeholder': 'Qué ha fallado, en una línea',
  'field.description.label': 'Qué ha ocurrido',
  'field.description.placeholder': '¿Qué estabas haciendo, qué esperabas y qué ocurrió en su lugar?',
  'field.severity.label': 'Impacto',
  'severity.blocker': 'Bloquea mi trabajo',
  'severity.major': 'Problema importante',
  'severity.minor': 'Problema menor',
  'severity.question': 'Pregunta',
  'capture.heading': 'Captura de sesión',
  'capture.on': 'Se adjuntarán los últimos {seconds} s de esta página.',
  'capture.off': 'La captura de sesión no está disponible. Tu informe se enviará igualmente.',
  'capture.noreplay':
    'Esta página no graba la pantalla. Tu informe incluye la dirección de la página y la actividad reciente de consola y red.',
  'capture.masked': 'Todo el texto y los valores introducidos están ocultos.',
  'capture.revealed': 'Se incluye el texto visible. Los valores introducidos siguen ocultos.',
  'reveal.label': 'Incluir el texto visible',
  'reveal.hint': 'Reinicia la captura: reproduce el problema de nuevo antes de enviarlo.',
  'context.heading': 'Adjuntado automáticamente',
  'action.submit': 'Enviar informe',
  'action.submitting': 'Enviando…',
  'action.cancel': 'Cancelar',
  'action.close': 'Cerrar',
  'result.ok': 'Gracias, hemos recibido tu informe.',
  'result.queued': 'Guardado. Se enviará la próxima vez que este sistema se conecte.',
  'result.error': 'No hemos podido enviar el informe: {error}',
  'error.required': 'Describe qué ha ocurrido.',
  'privacy.note': 'Las contraseñas y otros valores introducidos nunca se capturan.',
  'studio.title': 'Grabar una guía',
  'studio.subtitle': 'Graba una guía narrada para publicarla como contenido de ayuda.',
  'studio.name.label': 'Nombre de la guía',
  'studio.name.placeholder': 'p. ej. Publicar un perfil de Trust Center',
  'studio.action.start': 'Empezar a grabar',
  'studio.action.save': 'Guardar guía',
};

const fr: Catalogue = {
  'button.label': 'Signaler un problème',
  'dialog.title': 'Signaler un problème',
  'dialog.subtitle': "Dites-nous ce qui n'a pas fonctionné. Nous joignons les derniers instants de votre session afin de pouvoir le reproduire.",
  'field.title.label': 'Résumé',
  'field.title.placeholder': "Ce qui n'a pas fonctionné, en une ligne",
  'field.description.label': "Ce qui s'est passé",
  'field.description.placeholder': "Que faisiez-vous, qu'attendiez-vous et que s'est-il passé à la place ?",
  'field.severity.label': 'Impact',
  'severity.blocker': 'Bloque mon travail',
  'severity.major': 'Problème majeur',
  'severity.minor': 'Problème mineur',
  'severity.question': 'Question',
  'capture.heading': 'Capture de session',
  'capture.on': 'Les {seconds} dernières secondes de cette page seront jointes.',
  'capture.off': "La capture de session est indisponible. Votre signalement sera tout de même envoyé.",
  'capture.noreplay':
    "Cette page n'enregistre pas l'écran. Votre signalement contient l'adresse de la page ainsi que l'activité récente de la console et du réseau.",
  'capture.masked': 'Tout le texte et les valeurs saisies sont masqués.',
  'capture.revealed': 'Le texte visible est inclus. Les valeurs saisies restent masquées.',
  'reveal.label': 'Inclure le texte visible',
  'reveal.hint': 'Redémarre la capture — veuillez reproduire le problème avant l’envoi.',
  'context.heading': 'Joint automatiquement',
  'action.submit': 'Envoyer le signalement',
  'action.submitting': 'Envoi…',
  'action.cancel': 'Annuler',
  'action.close': 'Fermer',
  'result.ok': 'Merci — votre signalement a bien été reçu.',
  'result.queued': 'Enregistré. Il sera envoyé à la prochaine connexion de ce système.',
  'result.error': "Impossible d'envoyer le signalement : {error}",
  'error.required': "Veuillez décrire ce qui s'est passé.",
  'privacy.note': 'Les mots de passe et autres valeurs saisies ne sont jamais capturés.',
  'studio.title': 'Enregistrer une démonstration',
  'studio.subtitle': "Enregistrez une démonstration commentée à publier comme contenu d'aide.",
  'studio.name.label': 'Nom de la démonstration',
  'studio.name.placeholder': 'ex. Publier un profil Trust Center',
  'studio.action.start': "Démarrer l'enregistrement",
  'studio.action.save': 'Enregistrer la démonstration',
};

const de: Catalogue = {
  'button.label': 'Problem melden',
  'dialog.title': 'Problem melden',
  'dialog.subtitle': 'Beschreiben Sie, was schiefgelaufen ist. Wir hängen die letzten Momente Ihrer Sitzung an, damit wir es nachstellen können.',
  'field.title.label': 'Zusammenfassung',
  'field.title.placeholder': 'Was ist schiefgelaufen – in einer Zeile',
  'field.description.label': 'Was ist passiert',
  'field.description.placeholder': 'Was haben Sie getan, was haben Sie erwartet und was ist stattdessen passiert?',
  'field.severity.label': 'Auswirkung',
  'severity.blocker': 'Blockiert meine Arbeit',
  'severity.major': 'Schwerwiegendes Problem',
  'severity.minor': 'Kleineres Problem',
  'severity.question': 'Frage',
  'capture.heading': 'Sitzungsaufzeichnung',
  'capture.on': 'Die letzten {seconds} Sekunden dieser Seite werden angehängt.',
  'capture.off': 'Die Sitzungsaufzeichnung ist nicht verfügbar. Ihre Meldung wird trotzdem gesendet.',
  'capture.noreplay':
    'Diese Seite zeichnet den Bildschirm nicht auf. Ihre Meldung enthält die Seitenadresse sowie die jüngste Konsolen- und Netzwerkaktivität.',
  'capture.masked': 'Sämtlicher Text und alle Eingaben sind ausgeblendet.',
  'capture.revealed': 'Sichtbarer Text wird einbezogen. Eingaben bleiben ausgeblendet.',
  'reveal.label': 'Sichtbaren Text einbeziehen',
  'reveal.hint': 'Startet die Aufzeichnung neu – bitte stellen Sie das Problem vor dem Senden erneut nach.',
  'context.heading': 'Automatisch angehängt',
  'action.submit': 'Meldung senden',
  'action.submitting': 'Wird gesendet…',
  'action.cancel': 'Abbrechen',
  'action.close': 'Schließen',
  'result.ok': 'Vielen Dank – Ihre Meldung ist eingegangen.',
  'result.queued': 'Gespeichert. Sie wird gesendet, sobald dieses System das nächste Mal verbunden ist.',
  'result.error': 'Die Meldung konnte nicht gesendet werden: {error}',
  'error.required': 'Bitte beschreiben Sie, was passiert ist.',
  'privacy.note': 'Passwörter und andere Eingaben werden niemals aufgezeichnet.',
  'studio.title': 'Anleitung aufzeichnen',
  'studio.subtitle': 'Zeichnen Sie eine kommentierte Anleitung auf, um sie als Hilfeinhalt zu veröffentlichen.',
  'studio.name.label': 'Name der Anleitung',
  'studio.name.placeholder': 'z. B. Ein Trust-Center-Profil veröffentlichen',
  'studio.action.start': 'Aufzeichnung starten',
  'studio.action.save': 'Anleitung speichern',
};

const ptBR: Catalogue = {
  'button.label': 'Relatar um problema',
  'dialog.title': 'Relatar um problema',
  'dialog.subtitle': 'Conte o que deu errado. Anexamos os últimos momentos da sua sessão para conseguirmos reproduzir.',
  'field.title.label': 'Resumo',
  'field.title.placeholder': 'O que deu errado, em uma linha',
  'field.description.label': 'O que aconteceu',
  'field.description.placeholder': 'O que você estava fazendo, o que esperava e o que aconteceu no lugar?',
  'field.severity.label': 'Impacto',
  'severity.blocker': 'Impede meu trabalho',
  'severity.major': 'Problema grave',
  'severity.minor': 'Problema menor',
  'severity.question': 'Dúvida',
  'capture.heading': 'Captura da sessão',
  'capture.on': 'Os últimos {seconds}s desta página serão anexados.',
  'capture.off': 'A captura da sessão não está disponível. Seu relato será enviado mesmo assim.',
  'capture.noreplay':
    'Esta página não grava a tela. Seu relato leva o endereço da página e a atividade recente de console e rede.',
  'capture.masked': 'Todo o texto e os valores digitados estão ocultos.',
  'capture.revealed': 'O texto visível será incluído. Os valores digitados continuam ocultos.',
  'reveal.label': 'Incluir o texto visível',
  'reveal.hint': 'Reinicia a captura — reproduza o problema novamente antes de enviar.',
  'context.heading': 'Anexado automaticamente',
  'action.submit': 'Enviar relato',
  'action.submitting': 'Enviando…',
  'action.cancel': 'Cancelar',
  'action.close': 'Fechar',
  'result.ok': 'Obrigado — recebemos seu relato.',
  'result.queued': 'Salvo. Será enviado na próxima vez que este sistema se conectar.',
  'result.error': 'Não foi possível enviar o relato: {error}',
  'error.required': 'Descreva o que aconteceu.',
  'privacy.note': 'Senhas e outros valores digitados nunca são capturados.',
  'studio.title': 'Gravar um tutorial',
  'studio.subtitle': 'Grave um tutorial narrado para publicar como conteúdo de ajuda.',
  'studio.name.label': 'Nome do tutorial',
  'studio.name.placeholder': 'ex.: Publicar um perfil do Trust Center',
  'studio.action.start': 'Iniciar gravação',
  'studio.action.save': 'Salvar tutorial',
};

const ja: Catalogue = {
  'button.label': '問題を報告',
  'dialog.title': '問題を報告',
  'dialog.subtitle': '不具合の内容をお知らせください。再現できるよう、直前のセッションの記録を添付します。',
  'field.title.label': '概要',
  'field.title.placeholder': '不具合の内容を1行で',
  'field.description.label': '発生した内容',
  'field.description.placeholder': '何をしていて、どうなるはずで、実際には何が起きましたか。',
  'field.severity.label': '影響度',
  'severity.blocker': '業務が止まる',
  'severity.major': '重大な問題',
  'severity.minor': '軽微な問題',
  'severity.question': '質問',
  'capture.heading': 'セッションの記録',
  'capture.on': 'このページの直近 {seconds} 秒を添付します。',
  'capture.off': 'セッションの記録は利用できません。報告はそのまま送信されます。',
  'capture.noreplay':
    'このページでは画面を録画しません。報告にはページのアドレスと直近のコンソール・ネットワークの記録が含まれます。',
  'capture.masked': 'すべてのテキストと入力値は隠されています。',
  'capture.revealed': '表示されているテキストを含めます。入力値は引き続き隠されます。',
  'reveal.label': '表示されているテキストを含める',
  'reveal.hint': '記録を取り直します。送信前にもう一度問題を再現してください。',
  'context.heading': '自動的に添付されます',
  'action.submit': '報告を送信',
  'action.submitting': '送信中…',
  'action.cancel': 'キャンセル',
  'action.close': '閉じる',
  'result.ok': 'ご報告ありがとうございます。受け付けました。',
  'result.queued': '保存しました。次回この環境が接続されたときに送信されます。',
  'result.error': '報告を送信できませんでした: {error}',
  'error.required': '発生した内容をご記入ください。',
  'privacy.note': 'パスワードなどの入力値が記録されることはありません。',
  'studio.title': '操作手順を録画',
  'studio.subtitle': 'ヘルプとして公開する操作手順を録画します。',
  'studio.name.label': '手順の名前',
  'studio.name.placeholder': '例: Trust Center プロフィールを公開する',
  'studio.action.start': '録画を開始',
  'studio.action.save': '手順を保存',
};

const CATALOGUES: Record<string, Catalogue> = {
  en,
  es,
  fr,
  de,
  pt: ptBR,
  'pt-br': ptBR,
  ja,
};

/** Locales this build ships. Exposed so a settings UI can offer them. */
export const SUPPORTED_LOCALES = ['en', 'es', 'fr', 'de', 'pt-BR', 'ja'] as const;

/**
 * Resolve a BCP-47 tag to a catalogue.
 * Exact match ("pt-BR") wins, then the base language ("pt"), then English.
 */
export function resolveCatalogue(tag?: string): { locale: string; catalogue: Catalogue } {
  const raw = (tag || (typeof navigator !== 'undefined' ? navigator.language : '') || 'en').trim();
  const lower = raw.toLowerCase();
  if (CATALOGUES[lower]) return { locale: raw, catalogue: CATALOGUES[lower]! };
  const base = lower.split('-')[0]!;
  if (CATALOGUES[base]) return { locale: raw, catalogue: CATALOGUES[base]! };
  return { locale: 'en', catalogue: en };
}

/**
 * Build a translator. Missing keys fall back to English per key, so a partial
 * translation degrades one string at a time rather than showing a raw key.
 */
export function makeTranslator(tag?: string) {
  const { locale, catalogue } = resolveCatalogue(tag);
  const t = (key: StringKey, vars?: Record<string, string | number>): string => {
    const template = catalogue[key] ?? en[key];
    if (!vars) return template;
    return template.replace(/\{(\w+)\}/g, (m, k) => (k in vars ? String(vars[k]) : m));
  };
  return { locale, t };
}

export type Translator = ReturnType<typeof makeTranslator>['t'];
